// The DATA-TYPE slot inside a tabular row source's COLUMNS list.
//
//   MySQL / Oracle   JSON_TABLE(doc, '$[*]' COLUMNS ( tag ▮ PATH '$' ))
//   Oracle / PG      XMLTABLE('/r' PASSING x COLUMNS ( name ▮ PATH 'n' ))
//   T-SQL            OPENJSON(@doc, '$.a') WITH ( name ▮ '$.x' )
//   nested           … COLUMNS ( NESTED PATH '$.k' COLUMNS ( t ▮ PATH '$' ))
//
// Every one of these declares a column with a data type, and every one of them
// sits inside a FROM clause — so the text clause machine names the caret
// `from` (confidently) and neither the DDL family's override nor the
// ambiguity gate can reach it. The consequence was that the only type
// vocabulary at the caret was the flat keyword dump, underneath the whole
// relation list (mysql coverage script.43 §4). `deriveContext.ts` therefore
// carries ONE narrow named-clause override for this slot kind, exactly as it
// already does for mssql's `ddl-index-column`.
//
// Two things this recognizer deliberately refuses:
//   - the NAME slot (`COLUMNS (▮`, `COLUMNS (a, ▮`) — the writer is inventing
//     a name there, which `intellisense/context/declaredNameSlot.ts` claims;
//   - MySQL's `PARTITION BY RANGE COLUMNS (a ▮` / `LIST COLUMNS (…)`, which
//     spell `COLUMNS (` with EXISTING column names inside. The guard is
//     structural rather than a keyword blacklist: a row-source COLUMNS list is
//     always nested inside the row-source call's OWN parens, and a partition
//     clause's is not nested in anything.
// Oracle's `GRAPH_TABLE(… COLUMNS (▮` is excluded by the same nesting rule
// (its owner is `GRAPH_TABLE`, not a row-source function) — that slot names
// graph element properties and the graph resolver answers it.

import type { ClauseToken } from './editorContextClause';
import { dropAttachedPartial, findEnclosingLparenIndex, upper } from './editorContextSlotShared';

/**
 * Cheap text pre-filter so the hot path (ordinary DML at a named clause) does
 * not grow a tokenize pass. Every shape above names its function before the
 * caret.
 */
const ROW_SOURCE_CALL_RE = /\b(?:JSON_TABLE|XMLTABLE|OPENJSON)\b/i;

/** True when `text` can possibly contain a row-source COLUMNS list. */
export function mayBeRowSourceColumns(text: string): boolean {
  return ROW_SOURCE_CALL_RE.test(text);
}

/** The row-source functions whose parens may contain a `COLUMNS (` list. */
const COLUMNS_LIST_OWNERS: ReadonlySet<string> = new Set(['JSON_TABLE', 'XMLTABLE']);

/**
 * True when the caret is the DATA-TYPE slot of a row-source COLUMNS entry:
 * the entry has exactly one complete name token before the caret, and the
 * enclosing unclosed paren opens a row-source column list.
 */
export function rowSourceColumnTypeSlot(
  tokens: readonly ClauseToken[],
  /** Cursor offset — distinguishes a caret ATTACHED to a half-typed type name
   *  (`tag VAR▮`, the same slot) from one separated by whitespace. */
  offset: number,
): boolean {
  const eff = dropAttachedPartial(tokens, offset);
  const lparenIdx = findEnclosingLparenIndex(eff);
  if (lparenIdx < 0) return false;
  if (!isRowSourceColumnsOpener(eff, lparenIdx)) return false;

  const entry = currentListEntry(eff, lparenIdx);
  // Zero tokens = the NAME slot; more than one = the type is already written
  // (or the entry opened a nested construct), so the caret has moved past it.
  if (entry.length !== 1) return false;
  const name = entry[0]!;
  if (name.kind !== 'identifier' && name.kind !== 'keyword') return false;
  // `NESTED PATH '$.k' COLUMNS (…)` opens a projection, not a typed column.
  return upper(name) !== 'NESTED';
}

/**
 * The tokens of the list entry the caret is in: everything after the opener or
 * the last top-level comma inside it.
 */
function currentListEntry(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): readonly ClauseToken[] {
  let entryStart = lparenIdx + 1;
  let depth = 0;
  for (let i = lparenIdx + 1; i < tokens.length; i += 1) {
    const t = tokens[i]!;
    if (t.kind === 'lparen') depth += 1;
    else if (t.kind === 'rparen') depth -= 1;
    else if (t.kind === 'comma' && depth === 0) entryStart = i + 1;
  }
  return tokens.slice(entryStart);
}

/**
 * True when the unclosed `(` at `lparenIdx` opens a row-source column list:
 * either `COLUMNS (` nested inside a `JSON_TABLE` / `XMLTABLE` call (or inside
 * another `COLUMNS` list — the `NESTED PATH` form), or T-SQL's
 * `OPENJSON(…) WITH (`.
 */
function isRowSourceColumnsOpener(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  const opener = upper(tokens[lparenIdx - 1]);
  if (opener === 'COLUMNS') {
    // The paren CONTAINING the COLUMNS paren must belong to the row-source
    // call. This is the guard that keeps `PARTITION BY RANGE COLUMNS (` out:
    // that list is not nested inside any unclosed paren.
    const parentIdx = findEnclosingLparenIndex(tokens.slice(0, lparenIdx));
    if (parentIdx < 0) return false;
    const owner = upper(tokens[parentIdx - 1]);
    return owner !== null && (COLUMNS_LIST_OWNERS.has(owner) || owner === 'COLUMNS');
  }
  if (opener === 'WITH') {
    // `OPENJSON(<args>) WITH (` — the call's parens are CLOSED before `WITH`.
    const rparenIdx = lparenIdx - 2;
    if (tokens[rparenIdx]?.kind !== 'rparen') return false;
    const callLparenIdx = matchingLparenIndex(tokens, rparenIdx);
    if (callLparenIdx < 1) return false;
    return upper(tokens[callLparenIdx - 1]) === 'OPENJSON';
  }
  return false;
}

/** Index of the `(` that the `)` at `rparenIdx` closes, or -1. */
function matchingLparenIndex(tokens: readonly ClauseToken[], rparenIdx: number): number {
  let depth = 0;
  for (let i = rparenIdx; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.kind === 'rparen') depth += 1;
    else if (t.kind === 'lparen') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}
